import { Power4 } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { gsap } from 'gsap/dist/gsap';
import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';

import BusinessButton from '../business-button/business-button';
import { COLORS } from '../business-button/business-button.constants';
import styles from './business-hub-gateway.module.scss';
import { IphoneShell } from './iphone-shell';
import classNames from 'classnames';

export interface Slide {
    title: string;
    description: string;
    imageAltText?: string;
    backgroundImage?: string;
    ctaText?: string;
    ctaUrl?: string;
}

export type Props = {
    backgroundColor?: string;
    title?: string;
    description?: string;
    slides: Slide[];
};

gsap.registerPlugin(ScrollTrigger);

const BusinessHubGateway: FC<Props> = (props) => {
    const { backgroundColor, title, description, slides } = props;

    const [isRendered, setIsRendered] = useState<boolean>(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const headingRef = useRef<HTMLDivElement>(null);
    const sections = useRef<HTMLElement[]>([]);

    useEffect(() => {
        () => {
            sections.current = [];
        };
    }, []);

    const ctx = useRef<gsap.Context>();
    useLayoutEffect(() => {
        if (!containerRef.current) return;
        if (!sections.current.length) return;
        if (isRendered) return;
        setIsRendered(true);

        ctx.current = gsap.context(() => {
            let tl = gsap.timeline({
                defaults: { duration: 1, ease: Power4.easeInOut },
                scrollTrigger: {
                    trigger: containerRef.current,
                    pinnedContainer: containerRef.current,
                    pin: true,
                    scrub: 1,
                    start: () => {
                        const containerHeight = containerRef.current?.getBoundingClientRect().height || 0;
                        const windowHeight = window.innerHeight;

                        if (containerHeight < windowHeight) {
                            return 'center center';
                        }

                        return 'bottom bottom';
                    },
                    end: `+=300%`,
                    invalidateOnRefresh: true,
                },
            });

            gsap.set(sections.current, {
                autoAlpha: 1,
                opacity: 0,
                y: (i) => {
                    return `${100 * i}vh`;
                },
                transformOrigin: 'top',
                xPercent: (index) => -100 * index,
            });

            for (let index = 1; index < sections.current.length; index++) {
                const section = sections.current[index];
                const prevSection = sections.current[index - 1];

                tl.to(
                    [section],
                    {
                        y: 0,
                        opacity: 1,
                        // scale: 1,
                    },
                    '+=1'
                ).to(
                    [prevSection],
                    {
                        opacity: 0,
                        // scale: 0.9,
                    },
                    '<'
                );
            }

            tl.set({}, {}, '+=4');
        });
    }, [containerRef, sections]);

    return (
        <div className={styles.container} style={{ '--data-bg-color': backgroundColor } as any}>
            <div ref={containerRef} className={styles.box}>
                <div ref={headingRef}>
                    <div className={styles.headingTitle}>{title}</div>
                    <div className={styles.headingDescription}>{description}</div>
                </div>
                <div className={styles.parallaxContainer}>
                    {slides?.map(({ title, description, backgroundImage, ctaText, ctaUrl }, idx) => (
                        <section
                            key={`section-${idx}`}
                            className={styles.section}
                            ref={(ref) => {
                                if (ref) sections.current.push(ref);
                            }}
                        >
                            <div className={styles.contentBox}>
                                <div className={styles.contentLeft}>
                                    <div className={classNames(styles.contentSection, styles.left)}>
                                        <h1 className={styles.title}>{title}</h1>
                                        <p className={styles.description}>{description}</p>
                                        {ctaText && ctaUrl ? (
                                            <BusinessButton
                                                className={styles.learnMore}
                                                link={ctaUrl}
                                                buttonText={ctaText}
                                                autoMargin={false}
                                                color={COLORS.WHITE}
                                            ></BusinessButton>
                                        ) : null}
                                    </div>
                                </div>
                                <div className={styles.contentRight}>
                                    <IphoneShell />
                                    <div className={styles.iphoneShellContainer}>
                                        <div
                                            className={styles.screen}
                                            style={{
                                                backgroundImage: `url(${backgroundImage})`,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default BusinessHubGateway;
